import { AlgoliaTracking } from '@/scripts/libs/algolia/types/analytics'
import { ALGOLIA_TRACKING, PRODUCTS_QUERYID } from '@/scripts/constants/algolia'
import wcs from '@/scripts/libs/wcs'
import aa, { InsightsEvent } from 'search-insights'
import makeDebug from 'debug'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const debug = makeDebug('Algolia:Analytics')

class AlgoliaAnalytics {
  constructor() {
    const {
      algolia: { apiKey, applicationId },
    } = wcs.getStoreConfig()

    const userTokenIsAlreadySet = !!localStorage.getItem('AlgoliaAnalyticsUserToken')

    if (!userTokenIsAlreadySet) {
      const userToken = window.utag_data?.User_Email_MD5
        ? window.utag_data.User_Email_MD5
        : `${Math.floor(Math.random() * 100000)}${new Date().getTime()}`

      localStorage.setItem('AlgoliaAnalyticsUserToken', userToken)
    }

    aa('init', {
      appId: applicationId,
      apiKey,
    })
  }

  getUserToken(): string {
    const userToken = localStorage.getItem('AlgoliaAnalyticsUserToken')
    return userToken ?? ''
  }

  trackFacetChange({ grouped, ungrouped }: AlgoliaTracking) {
    if (!grouped.objectIDs || grouped.objectIDs.length === 0) {
      return
    }

    const objectIDChunks = this.splitIntoChunks(grouped.objectIDs, 20)

    const groupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'facet_change',
          eventType: 'view',
          index: grouped.indexName,
          queryID: grouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    const ungroupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'facet_change',
          eventType: 'view',
          index: ungrouped.indexName,
          queryID: grouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    aa('sendEvents', [...groupedEvents, ...ungroupedEvents])
  }

  trackLoadMore({ grouped, ungrouped }: AlgoliaTracking) {
    if (!grouped.objectIDs || grouped.objectIDs.length === 0) {
      return
    }

    const objectIDChunks = this.splitIntoChunks(grouped.objectIDs, 20)

    const groupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'load_more',
          eventType: 'view',
          index: grouped.indexName,
          queryID: grouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    const ungroupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'load_more',
          eventType: 'view',
          index: ungrouped.indexName,
          queryID: grouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    aa('sendEvents', [...groupedEvents, ...ungroupedEvents])
  }

  private splitIntoChunks(array: Array<string>, chunkSize: number) {
    const chunks = []
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
  }

  trackPageLanding({ grouped, ungrouped }: AlgoliaTracking): void {
    if (!grouped.objectIDs || grouped.objectIDs.length === 0) {
      return
    }

    const objectIDChunks = this.splitIntoChunks(grouped.objectIDs, 20)

    const groupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'landing',
          eventType: 'view',
          index: grouped.indexName,
          queryID: grouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    const ungroupedEvents = objectIDChunks.map(
      (chunk) =>
        ({
          eventName: 'landing',
          eventType: 'view',
          index: ungrouped.indexName,
          queryID: ungrouped.queryID,
          objectIDs: chunk,
          userToken: this.getUserToken(),
        } as InsightsEvent)
    )

    aa('sendEvents', [...groupedEvents, ...ungroupedEvents])
  }

  trackMouseHover({ ungrouped }: AlgoliaTracking): void {
    const position = ungrouped.position ?? 0
    const objectID = Array.isArray(ungrouped.objectIDs) ? ungrouped.objectIDs[position] : ''

    aa('sendEvents', [
      {
        eventName: 'hover',
        eventType: 'view',
        index: ungrouped.indexName,
        queryID: ungrouped.queryID,
        objectIDs: [objectID],
        userToken: this.getUserToken(),
      },
    ])
  }

  trackPurchase(cart: any): void {
    const dataInStorage = localStorage.getItem('algoliaTracking')
    const myAccount = localStorage.getItem('myAccount') || '{}'
    if (dataInStorage) {
      const myAccountData = JSON.parse(myAccount)
      const { orderItem } = cart
      const keysOrderItem = Object.keys(orderItem || {})
      const entriesOrderItem = Object.entries(orderItem || {})
      const groupedObjectsID = keysOrderItem?.map((key) => orderItem[key].partNumber)

      const {
        algolia: { groupedIndex, ungroupedIndex },
      } = wcs.getStoreConfig()
      aa('sendEvents', [
        {
          eventName: 'Products Purchased',
          eventType: 'conversion',
          // due to problem with fix check with new version installed
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          eventSubtype: 'purchase',
          index: groupedIndex,
          objectIDs: groupedObjectsID,
          userToken: this.getUserToken(),
          objectData: this.setObjectData(entriesOrderItem),
          currency: myAccountData?.data?.CURRENCY,
          value: this.setTotal(cart?.orderDetails),
        },
        {
          eventName: 'Products Purchased',
          eventType: 'conversion',
          // due to problem with fix check with new version installed
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          eventSubtype: 'purchase',
          index: ungroupedIndex,
          objectIDs: groupedObjectsID,
          userToken: this.getUserToken(),
          objectData: this.setObjectData(entriesOrderItem),
          currency: myAccountData?.data?.CURRENCY,
          value: this.setTotal(cart?.orderDetails),
        },
      ])
    }
  }

  setObjectData(items: Array<any>) {
    const dataFromLocalStorage = localStorage.getItem(PRODUCTS_QUERYID) || '{}'
    const currentData = JSON.parse(dataFromLocalStorage)
    return items.map(([, item]) => ({
      price: parseFloat(item?.price || ''),
      quantity: parseFloat(item?.orderItemQuantity || ''),
      queryID: currentData[item?.partNumber] || '',
    }))
  }

  setTotal(orderDetails: {
    TotalAdjustment: string
    TotalShipping: string
    Subtotal: string
  }): number {
    const TotalAdjustment = parseFloat(orderDetails?.TotalAdjustment)
    const TotalShipping = parseFloat(orderDetails?.TotalShipping)
    const Subtotal = parseFloat(orderDetails?.Subtotal)
    return Subtotal + TotalShipping + TotalAdjustment
  }

  trackAddToBag({ grouped, ungrouped }: AlgoliaTracking, partNumber: string) {
    aa('sendEvents', [
      {
        eventName: 'cart_add',
        eventType: 'conversion',
        index: grouped.indexName,
        queryID: grouped.queryID,
        objectIDs: [partNumber],
        userToken: this.getUserToken(),
      },
      {
        eventName: 'cart_add',
        eventType: 'conversion',
        index: ungrouped.indexName,
        queryID: grouped.queryID,
        objectIDs: [partNumber],
        userToken: this.getUserToken(),
      },
    ])
  }
  setProductsQueryID(partNumber: string) {
    const dataFromLocalStorage = localStorage.getItem(PRODUCTS_QUERYID)
    const dataInStorageAlgolia = localStorage.getItem(ALGOLIA_TRACKING)
    if (dataInStorageAlgolia) {
      const currentData = JSON.parse(dataFromLocalStorage || '{}') || {}
      const currentDataAlgoliaTracking = JSON.parse(dataInStorageAlgolia) || {}
      const { algolia } = currentDataAlgoliaTracking
      const { grouped } = algolia
      currentData[partNumber] = grouped?.queryID || ''
      localStorage.setItem(PRODUCTS_QUERYID, JSON.stringify(currentData))
    }
  }
}

export default new AlgoliaAnalytics()
